import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import ViewExternalCommissionPaymentModal from './external_commission_payment/ViewExternalCommissionPaymentModal.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { effectiveOwnerCommissionPercent } from '../../utils/externalCommissionMetrics.js';
import { ArrowLeft, DollarSign, Users, Eye, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';


const ViewExternalCommission = () => {
  const { t } = useTranslation();
  const { externalCommissionId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewPaymentOpen, setIsViewPaymentOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [openPaymentMenuId, setOpenPaymentMenuId] = useState(null);

  const loadPayments = useCallback(async (data) => {
    setPayments(data?.payments || []);
  }, []);

  const fetchRecord = useCallback(async () => {
    try {
      const refreshRes = await externalCommissionService.refreshSettlement(externalCommissionId).catch(() => null);
      if (refreshRes?.data?.success && refreshRes?.data?.data) {
        setRecord(refreshRes.data.data);
        setPayments(refreshRes.data.data.payments || []);
        return;
      }
      const res = await externalCommissionService.getById(externalCommissionId);
      setRecord(res.data.data);
      await loadPayments(res.data.data);
    } catch {
      Alert.error('Failed to load record');
      navigate('/external-commissions');
    } finally {
      setLoading(false);
    }
  }, [externalCommissionId, navigate, loadPayments]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const fmt = (n) => `₹${Math.round(parseFloat(n || 0)).toLocaleString('en-IN')}`;

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setIsViewPaymentOpen(true);
  };

  const getStatusBadge = (status) => {
    const map = {
      PENDING: 'bg-yellow-100 text-yellow-700',
      PARTIAL: 'bg-blue-100 text-blue-700',
      PAID: 'bg-green-100 text-green-700',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading commission..." />
        </div>
      </DashboardLayout>
    );
  }
  if (!record) return null;

  const payRemaining = Math.round(parseFloat(record.remaining_pay_to_owner ?? 0));
  const collectRemaining = Math.round(parseFloat(record.remaining_receive_from_owner ?? 0));
  const netPay = Math.max(payRemaining - collectRemaining, 0);
  const netCollect = Math.max(collectRemaining - payRemaining, 0);
  const ownerShareOnToPay = Math.round(parseFloat(
    record.booked_agent_commission_json?.totals?.total_owner_share_on_to_pay
    ?? record.total_owner_share_on_to_pay
    ?? record.total_owner_share_due
    ?? 0,
  ));
  const payToOwnerDue = Math.round(parseFloat(
    record.booked_agent_commission_json?.totals?.total_pay_to_owner_due
    ?? record.total_pay_to_owner_due
    ?? 0,
  ));
  const receiveFromOwnerDue = Math.round(parseFloat(
    record.booked_agent_commission_json?.totals?.total_receive_from_owner_due
    ?? record.total_receive_from_owner_due
    ?? record.total_agent_commission_due
    ?? 0,
  ));
  const ownerCommissionPercent = effectiveOwnerCommissionPercent(record);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto">
        <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex flex-wrap items-center gap-2 sm:gap-3">
              <DollarSign className="w-6 h-6 text-buttonhoverTxt shrink-0" />
              Vehicle Owner Commission Details
            </h1>
            <p className="text-sm text-gray-500 mt-1 break-all">
              {record.external_commission_id} · ML Sheet: {record.ml_sheet_id} · {record.vehicle_owner_name}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 w-full lg:w-auto">
            <Button variant="primary" icon={Users} onClick={() => navigate('/external-commissions/bulk-settlement')} className="w-full sm:w-auto">
              Bulk Settlement
            </Button>
            <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/external-commissions/edit/${externalCommissionId}`)} className="w-full sm:w-auto">
              Edit
            </Button>
            <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/external-commissions')} className="w-full sm:w-auto">
              Back
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-orange-500 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Commission ID</p>
            <p className="text-lg sm:text-xl font-bold text-orange-600 break-all">{record.external_commission_id}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-blue-500 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">ML Sheet ID</p>
            <p className="text-lg sm:text-xl font-bold text-blue-600 break-all">{record.ml_sheet_id}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-green-500 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Balance Freight</p>
            <p className="text-lg sm:text-xl font-bold text-green-600">{fmt(record.balance_freight)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-gray-400 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Created</p>
            <p className="text-sm sm:text-base font-bold text-gray-800">{formatDate(record.created_at)}</p>
            <p className="text-xs text-gray-500 mt-1">{record.vehicle_owner_name}</p>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-6">
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-5">
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b border-gray-200">
                Freight Breakdown
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Total Freight</p>
                  <p className="text-sm font-bold text-secondary">{fmt(record.total_freight)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Paid at Origin</p>
                  <p className="text-sm font-bold text-secondary">{fmt(record.paid_freight)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Balance at Destination</p>
                  <p className="text-sm font-bold text-secondary">{fmt(record.balance_freight)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">{t('pages.shipment.title')}</p>
                  <p className="text-sm font-bold text-secondary">{record.shipment_count ?? '—'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 sm:p-5">
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b border-gray-200">
                Agent & Owner Split
              </h3>
              <div className="space-y-2 text-sm">
                {ownerCommissionPercent != null && !Number.isNaN(ownerCommissionPercent) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Owner Percentage</span>
                    <span className="font-semibold text-secondary">{ownerCommissionPercent}%</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Booking Agent Commission</span>
                  <span className="font-semibold text-purple-700">{fmt(record.total_agent_commission ?? record.company_share_on_balance)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vehicle Owner Share</span>
                  <span className="font-semibold text-green-700">{fmt(record.total_owner_share ?? record.owner_commission_amount)}</span>
                </div>
                {payToOwnerDue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pay to owner (paid-at-origin freight − commission)</span>
                    <span className="font-semibold text-blue-700">{fmt(payToOwnerDue)}</span>
                  </div>
                )}
                {ownerShareOnToPay > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Owner keeps from to-pay collection</span>
                    <span className="font-semibold text-green-700">{fmt(ownerShareOnToPay)}</span>
                  </div>
                )}
                {receiveFromOwnerDue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Commission to receive from owner</span>
                    <span className="font-bold text-purple-700">{fmt(receiveFromOwnerDue)}</span>
                  </div>
                )}
              </div>
              {(record.booked_agent_commission_json?.agents || []).length > 0 && (
                <>
                <div className="mt-4 hidden md:block overflow-x-auto">
                  <table className="w-full min-w-[420px] text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600">
                        <th className="px-2 py-2 text-left">Agent</th>
                        <th className="px-2 py-2 text-right">Freight</th>
                        <th className="px-2 py-2 text-right">Commission</th>
                        <th className="px-2 py-2 text-right">Remit</th>
                        <th className="px-2 py-2 text-right">Owner (to-pay)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {record.booked_agent_commission_json.agents.map((agent) => {
                        const ownerPct = effectiveOwnerCommissionPercent(record, agent);
                        const pctLabel = ownerPct != null && !Number.isNaN(ownerPct)
                          ? ` (${ownerPct}% owner)`
                          : '';
                        return (
                        <tr key={agent.agent_id} className="border-t">
                          <td className="px-2 py-2">{agent.agent_name}{pctLabel}</td>
                          <td className="px-2 py-2 text-right">{fmt(agent.total_freight)}</td>
                          <td className="px-2 py-2 text-right">{fmt(agent.agent_commission_amount)}</td>
                          <td className="px-2 py-2 text-right">{fmt(agent.agent_remit_to_owner)}</td>
                          <td className="px-2 py-2 text-right">{fmt(agent.owner_share_on_to_pay)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 md:hidden space-y-3">
                  {record.booked_agent_commission_json.agents.map((agent) => {
                    const ownerPct = effectiveOwnerCommissionPercent(record, agent);
                    const pctLabel = ownerPct != null && !Number.isNaN(ownerPct)
                      ? ` (${ownerPct}% owner)`
                      : '';
                    return (
                      <div key={agent.agent_id} className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                        <p className="text-sm font-semibold text-secondary mb-2">{agent.agent_name}{pctLabel}</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div><span className="text-gray-500">Freight:</span> <span className="font-semibold">{fmt(agent.total_freight)}</span></div>
                          <div><span className="text-gray-500">Commission:</span> <span className="font-semibold">{fmt(agent.agent_commission_amount)}</span></div>
                          <div><span className="text-gray-500">Remit:</span> <span className="font-semibold">{fmt(agent.agent_remit_to_owner)}</span></div>
                          <div><span className="text-gray-500">Owner (to-pay):</span> <span className="font-semibold">{fmt(agent.owner_share_on_to_pay)}</span></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5">
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b border-gray-200">
              Net Settlement (view only)
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Settlements are recorded from <strong>Agent Bulk Settlement</strong>. Net = Pay to owner − Commission to receive.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 text-sm">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs text-gray-500">Pay to owner (pending)</p>
                <p className="text-xl font-bold text-blue-700">{fmt(payRemaining)}</p>
              </div>
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                <p className="text-xs text-gray-500">Commission to receive (pending)</p>
                <p className="text-xl font-bold text-purple-700">{fmt(collectRemaining)}</p>
              </div>
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 col-span-2 sm:col-span-2">
                <p className="text-xs text-gray-500">Net action</p>
                <p className="text-xl font-bold text-orange-700">
                  {netPay > 0 ? `Pay ${fmt(netPay)} to owner` : netCollect > 0 ? `Get ${fmt(netCollect)} from owner` : 'Settled'}
                </p>
              </div>
            </div>
            {ownerShareOnToPay > 0 && (
              <p className="text-xs text-green-700 mt-3">Owner keeps {fmt(ownerShareOnToPay)} from to-pay freight at destination (no payment needed).</p>
            )}
            <p className="text-xs text-gray-500 mt-3">
              Status: <span className={`inline-block px-2 py-0.5 rounded font-semibold ${getStatusBadge(record.settlement_status || record.payment_status)}`}>
                {record.settlement_status || record.payment_status || 'PENDING'}
              </span>
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5">
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b border-gray-200">
              Settlement History
            </h3>
            {payments.length > 0 ? (
              <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">ID</th>
                      <th className="px-3 py-2 text-left">Direction</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Mode</th>
                      <th className="px-3 py-2 text-center">Synced</th>
                      <th className="px-3 py-2 text-center">View</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {payments.map((p) => (
                      <tr key={p.payment_id}>
                        <td className="px-3 py-2 font-mono text-xs">{p.payment_id}</td>
                        <td className="px-3 py-2">{
                          p.direction === 'PAY_TO_OWNER' ? 'Pay to Owner'
                            : p.direction === 'PAY_TO_AGENT' ? 'Pay to Agent'
                              : p.direction === 'RECEIVE_FROM_OWNER' ? 'Receive from Owner'
                                : (p.direction === 'RECEIVE_FROM_AGENT' || p.direction === 'COLLECT') ? 'Receive from Agent'
                                  : p.direction
                        }</td>
                        <td className="px-3 py-2 text-right">{fmt(p.amount)}</td>
                        <td className="px-3 py-2">{p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN') : '—'}</td>
                        <td className="px-3 py-2">{p.payment_mode || 'N/A'}</td>
                        <td className="px-3 py-2 text-center">{p.sync_shipments ? t('common.yes') : t('common.no')}</td>
                        <td className="px-3 py-2 text-center">
                          <button type="button" onClick={() => handleViewPayment(p)} className="text-blue-600 hover:text-blue-800" title={t("common.view")}>
                            <Eye className="w-4 h-4 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden space-y-3">
                {payments.map((p) => (
                  <div key={p.payment_id} className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <div className="min-w-0">
                        <p className="text-xs font-mono text-gray-500 truncate">{p.payment_id}</p>
                        <p className="text-sm font-semibold text-secondary mt-0.5">
                          {p.direction === 'PAY_TO_OWNER' ? 'Pay to Owner'
                            : p.direction === 'PAY_TO_AGENT' ? 'Pay to Agent'
                              : p.direction === 'RECEIVE_FROM_OWNER' ? 'Receive from Owner'
                                : (p.direction === 'RECEIVE_FROM_AGENT' || p.direction === 'COLLECT') ? 'Receive from Agent'
                                  : p.direction}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-xs font-bold text-gray-900">{fmt(p.amount)}</span>
                        <RowActionsMenu
                          isOpen={openPaymentMenuId === p.payment_id}
                          onToggle={() => setOpenPaymentMenuId(openPaymentMenuId === p.payment_id ? null : p.payment_id)}
                          onClose={() => setOpenPaymentMenuId(null)}
                          onView={() => handleViewPayment(p)}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-gray-500">Date:</span> <span className="font-medium">{p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN') : '—'}</span></div>
                      <div><span className="text-gray-500">Mode:</span> <span className="font-medium">{p.payment_mode || 'N/A'}</span></div>
                      <div><span className="text-gray-500">Synced:</span> <span className="font-medium">{p.sync_shipments ? t('common.yes') : t('common.no')}</span></div>
                    </div>
                  </div>
                ))}
              </div>
              </>
            ) : (
              <p className="text-gray-500 text-center py-4 text-sm">No payments recorded yet</p>
            )}
          </div>
        </div>
      </div>

      {isViewPaymentOpen && selectedPayment && (
        <ViewExternalCommissionPaymentModal
          payment={selectedPayment}
          vehicleOwnerName={record?.vehicle_owner_name}
          onClose={() => { setIsViewPaymentOpen(false); setSelectedPayment(null); }}
        />
      )}
    </DashboardLayout>
  );
};

export default ViewExternalCommission;
